import Checkbox, { type CheckboxProps } from "@mui/material/Checkbox";
import { type FC, useMemo } from "react";
import { useController } from "react-hook-form";
import * as Styled from "./Checkbox.styled";
import * as StyledCommon from "./Common.styled";
import { FormError } from "./FormError";

export type CheckboxInputProps = Omit<CheckboxProps, "name"> & {
    disabled?: boolean;
    name: string;
    isRequired?: boolean;
    label?: string;
    labelClickable?: boolean;
    LabelTextComponent?: React.ElementType<React.PropsWithChildren<{}>>;
    afterChange?: (checked: boolean) => void;
};

export const CheckboxInput: FC<CheckboxInputProps> = ({
    disabled,
    name,
    label,
    isRequired,
    labelClickable = true,
    LabelTextComponent = StyledCommon.InputLabel,
    afterChange,
    ...rest
}) => {
    // Use controller so the stored value is a real boolean, not implicit 'on'/undefined
    const { field, fieldState } = useController({ name, defaultValue: false });
    const { error } = fieldState;

    const renderedLabel = useMemo(() => {
        if (!label) return null;

        const labelText = (
            <LabelTextComponent>
                {label} {isRequired && <StyledCommon.InputRequiredMark />}
            </LabelTextComponent>
        );

        if (!labelClickable) return labelText;

        return <label htmlFor={`${name}-name`}>{labelText}</label>;
    }, [label, isRequired, labelClickable, name, LabelTextComponent]);

    return (
        <StyledCommon.InputRootWrapper>
            <StyledCommon.InputControlWrapper>
                <Checkbox
                    required={isRequired}
                    id={`${name}-name`}
                    icon={<Styled.NotCheckedIcon />}
                    checkedIcon={<Styled.CheckedIcon />}
                    disabled={disabled}
                    disableRipple
                    // Controlled binding ensures persistence after remount
                    checked={!!field.value}
                    onChange={(e) => {
                        field.onChange(e.target.checked);
                        afterChange?.(e.target.checked);
                    }}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    name={field.name}
                    {...rest}
                />
                {renderedLabel}
            </StyledCommon.InputControlWrapper>

            <FormError error={error} fieldName={name} />
        </StyledCommon.InputRootWrapper>
    );
};
